import { DropzoneOptions, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Icon, IconProps } from "components/icon/Icon";
import { useEffect } from "react";
import { toast } from "@/utils/toast";
import { useFileUpload } from "@/contexts/FileUploadContext";
import { PdfIcon } from "../icons/PdfIcon";
import { WordIcon } from "../icons/WordIcon";
import { useAnalytics } from "@/hooks/useAnalytics";
import { getContentType, getManuscriptExtension } from "@/shared/constants";

interface FileUploadDropZoneProps extends DropzoneOptions {
  maxSizeMB?: number;
  icon?: IconProps["name"];
  error?: string;
}

export const FileUploadDropZone = ({
  maxSizeMB,
  error: errorProp,
  ...rest
}: FileUploadDropZoneProps) => {
  const { t } = useTranslation(["components"]);
  const { setFile } = useFileUpload();
  const { logEvent } = useAnalytics();

  const dropzoneOptions: DropzoneOptions = {
    maxSize: maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined,
    useFsAccessApi: false,
    ...rest,
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
    acceptedFiles,
  } = useDropzone(dropzoneOptions);

  useEffect(() => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, [acceptedFiles, setFile]);

  useEffect(() => {
    if (fileRejections.length > 0) {
      const errorCode = fileRejections[0].errors[0].code;
      switch (errorCode) {
        case "file-invalid-type":
          toast.error(t("home:fileUpload.toast.errorFileInvalidFileType"));
          logEvent("file_validation_error", {
            reason: "invalid_file_type",
            fileType: getContentType(fileRejections[0].file),
            fileSize: fileRejections[0].file.size,
          });
          return;
        case "file-too-large":
          toast.error(t("home:fileUpload.toast.errorFileTooLarge"));
          logEvent("file_validation_error", {
            reason: "file_too_large",
            fileType: getContentType(fileRejections[0].file),
            fileSize: fileRejections[0].file.size,
          });
          return;
        case "too-many-files":
          toast.error(t("home:fileUpload.toast.errorTooManyFiles"));
          return;
        default:
          toast.error(t("home:fileUpload.toast.errorGeneric"));
      }
    }
  }, [fileRejections, errorProp, t, logEvent]);

  const getIcon = () => {
    if (fileRejections.length > 0) {
      return <Icon name="x" size="large" color="#EF4444" />;
    }
    if (acceptedFiles.length === 1) {
      switch (getManuscriptExtension(acceptedFiles[0])) {
        case "pdf":
          return (
            <div className="w-[1.375rem] h-[1.375rem] flex pl-[0.2rem] justify-center items-center">
              <PdfIcon />
            </div>
          );
        case "docx":
          return (
            <div className="w-[1.375rem] h-[1.375rem] flex justify-center items-center">
              <WordIcon />
            </div>
          );
        default:
          return <Icon name="cloud-upload" size="large" color="#6366F1" />;
      }
    }
    return <Icon name="cloud-upload" size="large" color="#6366F1" />;
  };

  const getTitle = () => {
    if (acceptedFiles && acceptedFiles.length === 1) {
      return acceptedFiles[0].name.length > 50
        ? `${acceptedFiles[0].name.substring(0, 50)}...`
        : acceptedFiles[0].name;
    }
    if (isDragActive) {
      return t("components:fileUpload.dropZone.dropFiles");
    }
    return t("components:fileUpload.dropZone.message");
  };

  const getSubTitle = () => {
    if (acceptedFiles && acceptedFiles.length === 1) {
      const fileSizeKb = acceptedFiles[0].size / 1000;
      const fileSizeMb = fileSizeKb / 1000;
      const fileType = getManuscriptExtension(acceptedFiles[0]).toUpperCase();
      return `${fileType} • ${fileSizeMb > 1 ? `${fileSizeMb.toFixed(2)} MB` : `${fileSizeKb.toFixed(2)} KB`}`;
    }
    return t("components:fileUpload.dropZone.subtitle");
  };

  return (
    <div
      {...getRootProps()}
      className="flex flex-col items-center justify-center py-[4.5rem] gap-1 rounded-2xl bg-secondary-25 border-secondary-200 hover:cursor-pointer"
      data-testid="drop-zone"
    >
      <input {...getInputProps()} data-testid="file-upload" />
      <div className="flex flex-col min-w-[15.25rem] xl:mx-[10rem] xs:mx-[5rem] items-center justify-center rounded-lg">
        <div
          className={`flex items-center justify-center p-[0.8rem] mb-[0.75rem] rounded-full w-fit ${fileRejections.length > 0 ? "bg-red-100" : "bg-primary-50"}`}
        >
          {getIcon()}
        </div>
      </div>
      <div className="flex flex-col items-center justify-start gap-[0.75rem]">
        <p className="text-md leading-6 text-foreground truncate">
          {getTitle()}
        </p>
        <p className="text-xs font-normal text-muted-foreground leading-4 gap-[0.75rem]">
          {getSubTitle()}
        </p>
      </div>
    </div>
  );
};
